import { useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import enterpriseClient from '../clients/EnterpriseClient'
import { ApiError } from '../clients/ApiClient'
import {
  CategorisationAttempt,
  CategorisationTreeOption,
  CategorisationTreeQuestion,
  Submission,
  SubmissionAnswer,
  RiskWarning,
} from '../types'
import useAsyncEffect from '../util/useAsyncEffect'
import RiskWarningForm from '../screens/RiskWarningForm'
import CategorySelectionForm from '../screens/CategorySelectionForm'
import {
  AssessmentStatus,
  CategorisationStatus,
  Decision,
  RiskWarningStatus,
  SearchParams,
  SessionStorageKeys,
  SubmissionStatus,
} from '../enums'
import CancelFlow from '../screens/CancelFlow'
import CategorisationForm from '../screens/CategorisationForm'
import CategorisationFeedback from '../screens/CategorisationFeedback'
import ConfirmationOfAcceptance from '../screens/ConfirmationOfAcceptance'
import AppropriatenessAssessmentForm from '../screens/AppropriatenessAssessmentForm'
import { handleError } from '../util/error'
import {
  BadUrlError,
  deriveBootstrapErrorCause,
  deriveErrorCause,
  ErrorCause,
} from '../util/errorCause'
import ErrorScreen from '../screens/ErrorScreen'
import ZContainer from '../components/ZContainer'
import PreAppropriatenessAssessment from '../screens/PreAppropriatenessAssessment'
import CloseWebApp from '../screens/CloseWebApp'
import AppropriatenessAssessmentCompleted from '../screens/AppropriatenessAssessmentCompleted'
import ReadyToInvest from '../screens/ReadyToInvest'
import AcceptanceRejected from '../screens/AcceptanceRejected'
import config from '../config'
import { ConfigUtils } from '../util/configUtils'
import { assertNever } from '../util/assertNever'
import { latestByAttempt } from '../util/categorisationTree'
import {
  isRejectedThisCycle,
  resolveRiskAcceptanceStep,
  RiskAcceptanceStep,
} from '../util/riskAcceptanceStep'
import { returnToApp } from '../util/notifyHost'

type Step =
  | { name: 'loading' }
  | { name: 'riskWarning'; riskWarning: RiskWarning }
  | {
      name: 'categorySelection'
      submission: Submission
      categorisation: CategorisationAttempt
    }
  | {
      name: 'categorisation'
      submission: Submission
      categorisation: CategorisationAttempt
      rootOption: CategorisationTreeOption
    }
  | {
      name: 'categorisationNone'
      submission: Submission
      categorisation: CategorisationAttempt
      noneOption: CategorisationTreeOption
    }
  | { name: 'categorisationFeedback'; submission: Submission }
  | { name: 'preAssessment'; submission: Submission; referred: boolean }
  | { name: 'assessment'; submission: Submission }
  | { name: 'assessmentCompleted'; submission: Submission }
  | RiskAcceptanceStep
  | { name: 'flowEnded' }

const rootQuestionOf = (
  categorisation: CategorisationAttempt,
): CategorisationTreeQuestion => categorisation.questions[0]

const Home = () => {
  const [searchParams] = useSearchParams()

  const [step, setStep] = useState<Step>({ name: 'loading' })
  const [errorCause, setErrorCause] = useState<ErrorCause | null>(null)
  const [declinedRiskWarning, setDeclinedRiskWarning] = useState<boolean>(false)
  const [cancelCategorisation, setCancelCategorisation] =
    useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)

  // The init effect performs non-idempotent POSTs (e.g. risk warning
  // creation) — guard against double-invocation (React 18 StrictMode).
  const initialisedRef = useRef<boolean>(false)

  const handleEndOfFlow = () => {
    const authCode = sessionStorage.getItem(SessionStorageKeys.AuthCode)
    const deepLinkURI = ConfigUtils.getDeepLinkURIForOrganisation(
      sessionStorage.getItem(SessionStorageKeys.OrganisationId)!,
    )
    const returned = returnToApp(authCode!, deepLinkURI)
    if (!returned) {
      setStep({ name: 'flowEnded' })
    }
    setSubmitting(false)
  }

  // Resume the risk warning with a guaranteed-PENDING warning. createRiskWarning
  // is idempotent: it returns the current PENDING warning, or starts a fresh one
  // when the latest was REJECTED. Reusing the in-session `step.riskWarning` would
  // otherwise re-submit a no-longer-PENDING (rejected) warning, which the backend
  // rejects — so resuming after a rejection required a full reload.
  const resumeRiskWarning = async () => {
    try {
      setSubmitting(true)
      const riskWarning = await enterpriseClient.createRiskWarning()
      sessionStorage.setItem(SessionStorageKeys.RiskWarningId, riskWarning.id)
      setDeclinedRiskWarning(false)
      setStep({ name: 'riskWarning', riskWarning })
    } catch (e) {
      handleError(e)
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Decide the step for a submission's current state. May create the next
   * pending resource (create endpoints are idempotent for pending attempts).
   */
  const resolveSubmissionStep = async (
    submission: Submission,
  ): Promise<Step> => {
    switch (submission.status) {
      case SubmissionStatus.CoolingOff:
        // The prompt only opens once the cron promotes the submission to PASSED.
        return { name: 'assessmentCompleted', submission }
      case SubmissionStatus.Passed:
        // A branch point, not a terminal state: a rejection also leaves the
        // submission PASSED, so the acceptance records decide.
        return resolveRiskAcceptanceStep(submission)
      case SubmissionStatus.Aborted:
      case SubmissionStatus.Expired:
        // Terminal — nothing to resume in this session
        return { name: 'flowEnded' }
      case SubmissionStatus.Failed: {
        const assessment = latestByAttempt(submission.assessments)

        // Legacy pre-AT-783 sign-rejections.
        if (assessment?.status === AssessmentStatus.Passed) {
          const riskAcceptances = await enterpriseClient.listRiskAcceptances()

          if (isRejectedThisCycle(riskAcceptances, submission)) {
            return { name: 'acceptanceRejected' }
          }
        }

        return { name: 'assessmentCompleted', submission }
      }
      case SubmissionStatus.Pending:
      default: {
        const categorisation = latestByAttempt(submission.categorisations)

        if (
          !categorisation ||
          categorisation.status === CategorisationStatus.Pending
        ) {
          const attempt =
            categorisation ??
            (await enterpriseClient.createCategorisation(submission.id))
          return {
            name: 'categorySelection',
            submission,
            categorisation: attempt,
          }
        }

        if (categorisation.status === CategorisationStatus.Failed) {
          return { name: 'categorisationFeedback', submission }
        }

        // Passed or Referred → assessment stage
        const referred = categorisation.status === CategorisationStatus.Referred
        const assessment = latestByAttempt(submission.assessments)

        if (!assessment) {
          await enterpriseClient.createAssessment(submission.id)
          const refreshed = await enterpriseClient.getSubmission(submission.id)
          return { name: 'preAssessment', submission: refreshed, referred }
        }

        if (assessment.status === AssessmentStatus.Pending) {
          return assessment.attempt > 1
            ? { name: 'assessment', submission }
            : { name: 'preAssessment', submission, referred }
        }

        // Passed or Failed
        return { name: 'assessmentCompleted', submission }
      }
    }
  }

  // Only ApiError's message is backend-authored copy safe to show verbatim;
  // anything else (a raw fetch/runtime error) falls back to generic wording.
  const toastRecoverableError = (e: unknown) => {
    handleError(e instanceof ApiError ? e : undefined, 'Something went wrong')
  }

  /**
   * Run the tail of a submit the backend has already accepted — the refresh and
   * the step resolve, both of which issue requests. Anything in here failing
   * must not leave the customer on the form they just submitted, whose Submit
   * could only 400, so escalate: a reload resolves from the persisted state.
   * Wrap only the post-commit work; a failed submit stays a retryable toast.
   */
  const advanceAfterSubmit = async (advance: () => Promise<void>) => {
    try {
      await advance()
    } catch (e) {
      const cause = deriveErrorCause(e)
      if (cause === 'recoverable') {
        toastRecoverableError(e)
      }
      setErrorCause(cause)
    }
  }

  const resolveSubmission = async (
    urlSubmissionId?: string | null,
  ): Promise<Submission> => {
    if (urlSubmissionId) {
      return enterpriseClient.getSubmission(urlSubmissionId)
    }

    // List-first: createSubmission gates on *user* status, and for an APPROVED
    // user with a PASSED submission it silently starts a second one. Only
    // create when there is genuinely nothing to resume.
    const submissions = await enterpriseClient.listSubmissions()
    const latest = [...submissions].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    )[0]

    if (!latest) {
      // Brand-new user (e.g. just accepted the risk warning).
      return enterpriseClient.createSubmission()
    }

    if (latest.status === SubmissionStatus.Expired) {
      // Yearly re-do: start a fresh submission. If the backend isn't ready to
      // issue one, fall back to showing the expired journey.
      try {
        return await enterpriseClient.createSubmission()
      } catch {
        return enterpriseClient.getSubmission(latest.id)
      }
    }

    // Resume / display the existing journey; getSubmission also stamps the id
    // into sessionStorage.
    return enterpriseClient.getSubmission(latest.id)
  }

  const enterSubmissionFlow = async (urlSubmissionId?: string | null) => {
    const submission = await resolveSubmission(urlSubmissionId)
    setStep(await resolveSubmissionStep(submission))
  }

  useAsyncEffect(async () => {
    if (initialisedRef.current) {
      return
    }
    initialisedRef.current = true

    try {
      const authCode = searchParams.get(SearchParams.AuthCode)
      const codeVerifier = searchParams.get(SearchParams.CodeVerifier)
      const organisationId =
        searchParams.get(SearchParams.OrganisationId) ??
        config.manualTestingOrgId
      const submissionId = searchParams.get(SearchParams.SubmissionId)

      if (!authCode || !codeVerifier) {
        throw new BadUrlError()
      }

      sessionStorage.setItem(SessionStorageKeys.OrganisationId, organisationId)
      sessionStorage.setItem(SessionStorageKeys.AuthCode, authCode)
      sessionStorage.setItem(SessionStorageKeys.CodeVerifier, codeVerifier)

      // Risk warning is user-level and must come first.
      // POST /risk-warnings is NOT idempotent — check existing state via GET.
      const riskWarnings = await enterpriseClient.listRiskWarnings()

      const accepted = riskWarnings.find(
        w => w.status === RiskWarningStatus.Accepted,
      )

      if (accepted) {
        return await enterSubmissionFlow(submissionId)
      }

      const pending = riskWarnings.find(
        w => w.status === RiskWarningStatus.Pending,
      )

      const riskWarning =
        pending ?? (await enterpriseClient.createRiskWarning())

      sessionStorage.setItem(SessionStorageKeys.RiskWarningId, riskWarning.id)

      setStep({ name: 'riskWarning', riskWarning })
    } catch (e) {
      const hasAccessToken = !!sessionStorage.getItem(
        SessionStorageKeys.AccessToken,
      )
      const cause = deriveBootstrapErrorCause(e, hasAccessToken)
      if (cause === 'recoverable') {
        toastRecoverableError(e)
      }
      setErrorCause(cause)
    }
  }, [])

  const submitCategorisationAnswers = async (
    submission: Submission,
    categorisation: CategorisationAttempt,
    answers: SubmissionAnswer[],
    signedAt: string,
  ) => {
    try {
      setSubmitting(true)
      const result = await enterpriseClient.submitCategorisation(
        submission.id,
        categorisation.id,
        answers,
        signedAt,
      )
      await advanceAfterSubmit(async () => {
        const refreshed = await enterpriseClient.getSubmission(submission.id)

        if (result.status === CategorisationStatus.Failed) {
          setStep({ name: 'categorisationFeedback', submission: refreshed })
        } else {
          setStep(await resolveSubmissionStep(refreshed))
        }
      })
    } catch (e) {
      handleError(e)
    } finally {
      setSubmitting(false)
    }
  }

  // Re-enter category selection with a guaranteed-PENDING attempt. createCategorisation
  // is idempotent: it returns the current attempt when one is PENDING and starts a fresh
  // one when the latest is FAILED. Reusing the in-session `step.categorisation` would
  // otherwise re-submit a stale (already FAILED) attempt, which the backend rejects with
  // "categorisation is not pending" — even though users are allowed to re-submit.
  const reenterCategorySelection = async (submission: Submission) => {
    try {
      setSubmitting(true)
      const categorisation = await enterpriseClient.createCategorisation(
        submission.id,
      )
      const refreshed = await enterpriseClient.getSubmission(submission.id)
      setStep({
        name: 'categorySelection',
        submission: refreshed,
        categorisation,
      })
    } catch (e) {
      handleError(e)
    } finally {
      setSubmitting(false)
    }
  }

  const renderStep = () => {
    const closeWebApp = () => (
      <CloseWebApp
        deepLinkURI={ConfigUtils.getDeepLinkURIForOrganisation(
          sessionStorage.getItem(SessionStorageKeys.OrganisationId)!,
        )}
      />
    )

    switch (step.name) {
      case 'loading':
        return (
          <ZContainer style={{ justifyContent: 'center' }}>
            <CircularProgress />
          </ZContainer>
        )

      case 'riskWarning': {
        if (declinedRiskWarning) {
          return (
            <CancelFlow
              body="If you skip these steps, this process will be cancelled and you will not be able to invest in cryptoassets."
              buttonText="Resume the process"
              cancelText="I wish to leave this investment journey"
              loading={submitting}
              onDecision={async proceed => {
                if (proceed) {
                  // Resume — ensure a PENDING warning (fresh if the last was rejected).
                  await resumeRiskWarning()
                  return
                }
                // signedAt = the moment the customer made the decision (click).
                const signedAt = new Date().toISOString()
                try {
                  setSubmitting(true)
                  await enterpriseClient.submitRiskWarning(
                    step.riskWarning.id,
                    {
                      decision: Decision.Reject,
                      signedAt,
                    },
                  )
                  handleEndOfFlow()
                } catch (e) {
                  handleError(e)
                  setSubmitting(false)
                }
              }}
            />
          )
        }

        return (
          <RiskWarningForm
            riskWarning={step.riskWarning}
            loading={submitting}
            onDecision={async (accepted, learnMoreViewedAt) => {
              if (!accepted) {
                setDeclinedRiskWarning(true)
                return
              }
              // signedAt = the moment the customer made the decision (click).
              const signedAt = new Date().toISOString()
              try {
                setSubmitting(true)
                await enterpriseClient.submitRiskWarning(step.riskWarning.id, {
                  decision: Decision.Accept,
                  signedAt,
                  learnMoreViewedAt,
                })
                await advanceAfterSubmit(async () => {
                  await enterSubmissionFlow()
                })
              } catch (e) {
                handleError(e)
              } finally {
                setSubmitting(false)
              }
            }}
          />
        )
      }

      case 'categorySelection': {
        if (cancelCategorisation) {
          return (
            <CancelFlow
              body="If you skip these steps, this process will be cancelled and you will not be able to invest in cryptoassets."
              buttonText="Resume the process"
              cancelText="I wish to leave this investment journey"
              onDecision={proceed => {
                if (proceed) {
                  // let user re-do categorisation
                  setCancelCategorisation(false)
                } else {
                  handleEndOfFlow()
                }
              }}
            />
          )
        }

        return (
          <CategorySelectionForm
            rootQuestion={rootQuestionOf(step.categorisation)}
            onSelectOption={option => {
              if (option.value === 'NONE') {
                setStep({
                  name: 'categorisationNone',
                  submission: step.submission,
                  categorisation: step.categorisation,
                  noneOption: option,
                })
              } else {
                setStep({
                  name: 'categorisation',
                  submission: step.submission,
                  categorisation: step.categorisation,
                  rootOption: option,
                })
              }
            }}
            onCancel={() => setCancelCategorisation(true)}
          />
        )
      }

      case 'categorisationNone':
        return (
          <CancelFlow
            body={`As you have selected the category ‘None’, we strongly encourage you to consider if this type of investment is right for you and to seek financial advice.`}
            buttonText="Resubmit investor category"
            cancelText="I wish to leave this investment journey"
            loading={submitting}
            onDecision={async proceed => {
              if (proceed) {
                // let user re-categorise — ensure a fresh PENDING attempt
                await reenterCategorySelection(step.submission)
                return
              }

              try {
                setSubmitting(true)
                // signedAt = the moment the customer confirmed "none of the above".
                await enterpriseClient.submitCategorisation(
                  step.submission.id,
                  step.categorisation.id,
                  [
                    {
                      questionId: rootQuestionOf(step.categorisation).id,
                      answerId: step.noneOption.id,
                    },
                  ],
                  new Date().toISOString(),
                )
                handleEndOfFlow()
              } catch (e) {
                handleError(e)
                setSubmitting(false)
              }
            }}
          />
        )

      case 'categorisation':
        return (
          <CategorisationForm
            rootQuestion={rootQuestionOf(step.categorisation)}
            selectedOption={step.rootOption}
            loading={submitting}
            recategorise={() => reenterCategorySelection(step.submission)}
            onLeave={() => handleEndOfFlow()}
            onComplete={(answers, signedAt) =>
              submitCategorisationAnswers(
                step.submission,
                step.categorisation,
                answers,
                signedAt,
              )
            }
          />
        )

      case 'categorisationFeedback':
        return (
          <CategorisationFeedback
            loading={submitting}
            onDecision={async resubmit => {
              if (!resubmit) {
                handleEndOfFlow()
                return
              }
              // recategorisation may be rejected (e.g. submission failed) — the
              // helper surfaces that via handleError.
              await reenterCategorySelection(step.submission)
            }}
          />
        )

      case 'preAssessment':
        return (
          <PreAppropriatenessAssessment
            referred={step.referred}
            orgName={ConfigUtils.getNameForOrganisation(
              sessionStorage.getItem(SessionStorageKeys.OrganisationId)!,
            )}
            maxAttempts={step.submission.assessmentMaxAttempts}
            knowledgeHubUrl={ConfigUtils.getKnowledgeHubLinkForOrganisation(
              sessionStorage.getItem(SessionStorageKeys.OrganisationId)!,
            )}
            onDecision={proceed => {
              if (proceed) {
                setStep({ name: 'assessment', submission: step.submission })
              } else {
                handleEndOfFlow()
              }
            }}
          />
        )

      case 'assessment': {
        const assessment = latestByAttempt(step.submission.assessments)!

        return (
          <AppropriatenessAssessmentForm
            questions={assessment.questions}
            loading={submitting}
            onBack={() =>
              setStep({
                name: 'preAssessment',
                submission: step.submission,
                referred: false,
              })
            }
            onComplete={async answers => {
              // The assessment has no separate consent step, so signedAt is the
              // moment the customer commits to their answers (Submit click).
              const signedAt = new Date().toISOString()
              try {
                setSubmitting(true)
                await enterpriseClient.submitAssessment(
                  step.submission.id,
                  assessment.id,
                  answers,
                  signedAt,
                )
                await advanceAfterSubmit(async () => {
                  const refreshed = await enterpriseClient.getSubmission(
                    step.submission.id,
                  )
                  setStep(await resolveSubmissionStep(refreshed))
                })
              } catch (e) {
                handleError(e)
              } finally {
                setSubmitting(false)
              }
            }}
          />
        )
      }

      case 'assessmentCompleted': {
        const assessment = latestByAttempt(step.submission.assessments)

        if (!assessment) {
          // Submission finished without an assessment in this session
          return (
            <ZContainer style={{ justifyContent: 'center' }}>
              <CircularProgress />
            </ZContainer>
          )
        }

        return (
          <AppropriatenessAssessmentCompleted
            assessment={assessment}
            submission={step.submission}
            knowledgeHubUrl={ConfigUtils.getKnowledgeHubLinkForOrganisation(
              sessionStorage.getItem(SessionStorageKeys.OrganisationId)!,
            )}
            deepLinkURI={ConfigUtils.getDeepLinkURIForOrganisation(
              sessionStorage.getItem(SessionStorageKeys.OrganisationId)!,
            )}
            onDecision={async retry => {
              if (!retry) {
                handleEndOfFlow()
                return
              }

              try {
                setSubmitting(true)
                await enterpriseClient.createAssessment(step.submission.id)
                const refreshed = await enterpriseClient.getSubmission(
                  step.submission.id,
                )
                setStep({ name: 'assessment', submission: refreshed })
              } catch (e) {
                // Retry not available yet / no attempts remaining
                handleError(e)
              } finally {
                setSubmitting(false)
              }
            }}
          />
        )
      }

      case 'riskAcceptance':
        return (
          <ConfirmationOfAcceptance
            message={step.riskAcceptance.message}
            loading={submitting}
            onDecision={async accepted => {
              // signedAt = the moment the customer clicked accept/reject.
              const signedAt = new Date().toISOString()
              try {
                setSubmitting(true)
                await enterpriseClient.submitRiskAcceptance(
                  step.riskAcceptance.id,
                  {
                    decision: accepted ? Decision.Accept : Decision.Reject,
                    signedAt,
                  },
                )
                // Both outcomes are terminal; rendering the screen here keeps
                // the in-session and re-entry paths identical.
                setStep(
                  accepted
                    ? { name: 'readyToInvest' }
                    : { name: 'acceptanceRejected' },
                )
              } catch (e) {
                handleError(e)
              } finally {
                setSubmitting(false)
              }
            }}
          />
        )

      case 'acceptanceRejected':
        return (
          <AcceptanceRejected
            deepLinkURI={ConfigUtils.getDeepLinkURIForOrganisation(
              sessionStorage.getItem(SessionStorageKeys.OrganisationId)!,
            )}
          />
        )

      case 'readyToInvest':
        return <ReadyToInvest onStart={handleEndOfFlow} />

      case 'flowEnded':
        return closeWebApp()

      // `Step` spans two modules, so this keeps an unhandled member a build
      // error rather than a silent fall-through to CloseWebApp.
      default:
        return assertNever(step, closeWebApp())
    }
  }

  if (errorCause) {
    return (
      <ZContainer>
        <ErrorScreen
          cause={errorCause}
          onReload={() => window.location.reload()}
        />
      </ZContainer>
    )
  }

  return <ZContainer>{renderStep()}</ZContainer>
}

export default Home
